import React, { useState, useEffect } from 'react';
import { Activity, Calendar, BarChart3, PieChart, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getTodayForInput, getMonthsAgoForInput } from '../../utils/dateUtils';

// Custom Date Picker Component
const CustomDatePicker = ({ value, onChange, onClose }) => {
    const [tempDate, setTempDate] = useState(value ? new Date(value) : new Date());
    const [viewMonth, setViewMonth] = useState(tempDate.getMonth());
    const [viewYear, setViewYear] = useState(tempDate.getFullYear());

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    const getDaysInMonth = (year, month) => {
        return new Date(year, month + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (year, month) => {
        return new Date(year, month, 1).getDay();
    };

    const handleDateSelect = (day) => {
        const newDate = new Date(viewYear, viewMonth, day);
        setTempDate(newDate);
        onChange(newDate.toISOString().split('T')[0]);
        onClose();
    };

    const changeMonth = (delta) => {
        let newMonth = viewMonth + delta;
        let newYear = viewYear;
        if (newMonth < 0) {
            newMonth = 11;
            newYear--;
        } else if (newMonth > 11) {
            newMonth = 0;
            newYear++;
        }
        setViewMonth(newMonth);
        setViewYear(newYear);
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            className="absolute top-full right-0 mt-2 z-50 bg-white dark:bg-slate-900 
                     rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 
                     p-4 w-72"
        >
            <div className="flex items-center justify-between mb-4">
                <button
                    onClick={() => changeMonth(-1)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                >
                    <ChevronLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
                <span className="font-semibold text-sm text-slate-900 dark:text-white">
                    {months[viewMonth]} {viewYear}
                </span>
                <button
                    onClick={() => changeMonth(1)}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                >
                    <ChevronRight className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
                {days.map(day => (
                    <div key={day} className="text-center text-xs text-slate-500 dark:text-slate-400 py-1">
                        {day}
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: getFirstDayOfMonth(viewYear, viewMonth) }).map((_, i) => (
                    <div key={`empty-${i}`} className="h-8" />
                ))}
                {Array.from({ length: getDaysInMonth(viewYear, viewMonth) }).map((_, i) => {
                    const day = i + 1;
                    const isSelected = tempDate.getDate() === day && 
                                     tempDate.getMonth() === viewMonth && 
                                     tempDate.getFullYear() === viewYear;
                    const isToday = new Date().getDate() === day && 
                                  new Date().getMonth() === viewMonth && 
                                  new Date().getFullYear() === viewYear;
                    return (
                        <button
                            key={day}
                            onClick={() => handleDateSelect(day)}
                            className={`h-8 text-xs rounded-lg transition-all duration-200
                                      ${isSelected 
                                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md' 
                                          : isToday
                                          ? 'border border-emerald-500 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                          : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'
                                      }`}
                        >
                            {day}
                        </button>
                    );
                })}
            </div>
        </motion.div>
    );
};

// Date Range Component
const DateRangeDisplay = ({ fromDate, toDate, onFromClick, onToClick, activePicker }) => {
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const [year, month, day] = dateStr.split('-');
        return `${day}-${month}-${year}`;
    };

    return (
        <div className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-700 text-[12px] text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-900">
            <Calendar className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
            <button 
                onClick={onFromClick}
                className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors ${activePicker === 'from' ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : ''}`}
            >
                {formatDate(fromDate)}
            </button>
            <span>—</span>
            <button 
                onClick={onToClick}
                className={`hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors ${activePicker === 'to' ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : ''}`}
            >
                {formatDate(toDate)}
            </button>
        </div>
    );
};

const LANES = [
    { name: 'Shanghai → Los Angeles', score: 95, delays: 2, color: 'bg-emerald-500' },
    { name: 'Rotterdam → New York', score: 78, delays: 8, color: 'bg-amber-500' },
    { name: 'Singapore → London', score: 88, delays: 4, color: 'bg-emerald-500' },
    { name: 'Hamburg → Dubai', score: 92, delays: 3, color: 'bg-emerald-500' },
    { name: 'Mumbai → Rotterdam', score: 82, delays: 6, color: 'bg-amber-500' },
    { name: 'Los Angeles → Tokyo', score: 91, delays: 5, color: 'bg-emerald-500' },
];

export function LanePerformance({ fromDate, toDate, onFromClick, onToClick, activePicker }) {
    return (
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white flex items-center justify-center shadow-md">
                        <Activity className="w-5 h-5" />
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 dark:text-white">Lane Performance Score</div>
                </div>
                <DateRangeDisplay 
                    fromDate={fromDate} 
                    toDate={toDate} 
                    onFromClick={() => onFromClick('lane')}
                    onToClick={() => onToClick('lane')}
                    activePicker={activePicker?.card === 'lane' ? activePicker.type : null}
                />
            </div>
            <div className="p-5 space-y-3.5">
                {LANES.map((l) => (
                    <div key={l.name}>
                        <div className="flex items-center justify-between text-[12.5px] mb-1.5">
                            <span className="text-slate-800 dark:text-slate-200 font-medium">{l.name}</span>
                            <div className="flex items-center gap-3">
                                <span className="text-[13px] font-bold text-slate-900 dark:text-white tabular-nums">{l.score}%</span>
                                <span className="text-[11px] text-slate-500 dark:text-slate-400 w-14 text-right">{l.delays} delays</span>
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                            <div className={`h-full rounded-full ${l.color}`} style={{ width: `${l.score}%` }} />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

const CARRIERS = [
    { name: 'Maersk', value: 92 },
    { name: 'MSC', value: 88 },
    { name: 'CMA CGM', value: 85 },
    { name: 'COSCO', value: 78 },
    { name: 'Hapag-Lloyd', value: 95 },
    { name: 'ONE', value: 82 },
];

export function OnTimeCarriers({ fromDate, toDate, onFromClick, onToClick, activePicker }) {
    const max = 100;
    return (
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white flex items-center justify-center shadow-md">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 dark:text-white">On-Time Delivery by Carrier (%)</div>
                </div>
                <DateRangeDisplay 
                    fromDate={fromDate} 
                    toDate={toDate} 
                    onFromClick={() => onFromClick('carrier')}
                    onToClick={() => onToClick('carrier')}
                    activePicker={activePicker?.card === 'carrier' ? activePicker.type : null}
                />
            </div>
            <div className="p-5">
                <div className="flex items-center gap-2 mb-3 text-[11px] text-slate-600 dark:text-slate-400">
                    <span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> On-Time %
                </div>
                <div className="relative h-[260px] flex items-end gap-3 pl-10 pr-2">
                    <div className="absolute left-0 top-0 bottom-6 w-9 flex flex-col justify-between text-[10px] text-slate-400 dark:text-slate-500">
                        {[100, 80, 60, 40, 20, 0].map((v) => <span key={v}>{v}</span>)}
                    </div>
                    <div className="absolute left-9 right-2 top-0 bottom-6 flex flex-col justify-between">
                        {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className="border-t border-slate-100 dark:border-slate-800" />)}
                    </div>
                    {CARRIERS.map((c) => (
                        <div key={c.name} className="relative z-10 flex-1 flex flex-col items-center justify-end h-full">
                            <div className="relative w-full h-[calc(100%-1.5rem)] max-w-[44px] flex flex-col items-center justify-end group">
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150
                                               absolute -top-7 text-[11px] font-semibold text-slate-900 dark:text-white">
                                    {c.value}%
                                </span>
                                <div
                                    className="w-full rounded-t-md bg-gradient-to-t from-violet-500 to-fuchsia-400
                                             hover:from-violet-600 hover:to-fuchsia-500 transition-all duration-200
                                             shadow-[0_-6px_20px_-4px_rgba(139,92,246,0.4)]"
                                    style={{ height: `${(c.value / max) * 100}%` }}
                                />
                            </div>
                            <span className="mt-2 text-[10.5px] text-slate-600 dark:text-slate-400 text-center leading-tight">{c.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

const MODES = [
    { name: 'Ocean', value: 62, color: '#6366F1' },
    { name: 'Truck', value: 18, color: '#A855F7' },
    { name: 'Air', value: 12, color: '#EC4899' },
    { name: 'Rail', value: 8, color: '#F59E0B' },
];

export function ShipmentMode({ fromDate, toDate, onFromClick, onToClick, activePicker }) {
    const total = MODES.reduce((a, b) => a + b.value, 0);
    let cum = 0;
    const segs = MODES.map((m) => {
        const start = (cum / total) * 360;
        cum += m.value;
        const end = (cum / total) * 360;
        return { ...m, start, end };
    });

    return (
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 text-white flex items-center justify-center shadow-md">
                        <PieChart className="w-5 h-5" />
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 dark:text-white">Shipment Mode Distribution</div>
                </div>
                <DateRangeDisplay 
                    fromDate={fromDate} 
                    toDate={toDate} 
                    onFromClick={() => onFromClick('mode')}
                    onToClick={() => onToClick('mode')}
                    activePicker={activePicker?.card === 'mode' ? activePicker.type : null}
                />
            </div>
            <div className="p-5">
                <div className="flex flex-col items-center">
                    <div className="relative w-[220px] h-[220px]">
                        <svg viewBox="-110 -110 220 220" className="w-full h-full -rotate-90">
                            {segs.map((s) => {
                                const r = 90, ir = 56;
                                const a1 = (s.start * Math.PI) / 180;
                                const a2 = (s.end * Math.PI) / 180;
                                const large = s.end - s.start > 180 ? 1 : 0;
                                const x1 = Math.cos(a1) * r, y1 = Math.sin(a1) * r;
                                const x2 = Math.cos(a2) * r, y2 = Math.sin(a2) * r;
                                const x3 = Math.cos(a2) * ir, y3 = Math.sin(a2) * ir;
                                const x4 = Math.cos(a1) * ir, y4 = Math.sin(a1) * ir;
                                const d = [
                                    `M ${x1} ${y1}`,
                                    `A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`,
                                    `L ${x3} ${y3}`,
                                    `A ${ir} ${ir} 0 ${large} 0 ${x4} ${y4}`,
                                    'Z',
                                ].join(' ');
                                return <path key={s.name} d={d} fill={s.color}
                                           className="hover:opacity-90 transition-opacity"
                                           style={{ filter: `drop-shadow(0 4px 10px ${s.color}55)` }} />;
                            })}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-[24px] font-bold text-slate-900 dark:text-white tabular-nums leading-none">{total}%</div>
                            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">Total Volume</div>
                        </div>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2">
                        {MODES.map((m) => (
                            <div key={m.name} className="inline-flex items-center gap-2 text-[12px] text-slate-700 dark:text-slate-300">
                                <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: m.color }} />
                                {m.name}
                                <span className="text-slate-500 dark:text-slate-400 tabular-nums">{m.value}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

const BOOKINGS = [
    { name: 'MSC', value: 540, color: '#3B82F6' },
    { name: 'CMA-CGM', value: 80, color: '#EC4899' },
    { name: 'Hapag Lloyd', value: 110, color: '#10B981' },
    { name: 'Yang Ming', value: 60, color: '#F59E0B' },
    { name: 'Maersk', value: 220, color: '#8B5CF6' },
    { name: 'ONE', value: 50, color: '#06B6D4' },
    { name: 'OOCL', value: 90, color: '#EF4444' },
    { name: 'HMM', value: 70, color: '#84CC16' },
];

export function BookingsByCarrier({ fromDate, toDate, onFromClick, onToClick, activePicker }) {
    const max = 600;
    return (
        <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 text-white flex items-center justify-center shadow-md">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div className="text-[14px] font-semibold text-slate-900 dark:text-white">Bookings by Carrier</div>
                </div>
                <DateRangeDisplay 
                    fromDate={fromDate} 
                    toDate={toDate} 
                    onFromClick={() => onFromClick('bookings')}
                    onToClick={() => onToClick('bookings')}
                    activePicker={activePicker?.card === 'bookings' ? activePicker.type : null}
                />
            </div>
            <div className="p-5">
                <div className="flex items-center gap-2 mb-3 text-[11px] text-slate-600 dark:text-slate-400">
                    <span className="w-2.5 h-2.5 rounded-sm bg-cyan-500" /> Bookings
                </div>
                <div className="relative h-[260px] flex items-end gap-2 pl-10 pr-2">
                    <div className="absolute left-0 top-0 bottom-6 w-9 flex flex-col justify-between text-[10px] text-slate-400 dark:text-slate-500">
                        {[600, 500, 400, 300, 200, 100, 0].map((v) => <span key={v}>{v}</span>)}
                    </div>
                    <div className="absolute left-9 right-2 top-0 bottom-6 flex flex-col justify-between">
                        {[0, 1, 2, 3, 4, 5, 6].map((i) => <span key={i} className="border-t border-slate-100 dark:border-slate-800" />)}
                    </div>
                    {BOOKINGS.map((c) => (
                        <div key={c.name} className="relative z-10 flex-1 flex flex-col items-center justify-end h-full">
                            <div className="relative w-full h-[calc(100%-1.5rem)] max-w-[34px] flex flex-col items-center justify-end group">
                                <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-150
                                               absolute -top-7 text-[11px] font-semibold text-slate-900 dark:text-white">
                                    {c.value}
                                </span>
                                <div
                                    className="w-full rounded-t-md transition-all duration-200"
                                    style={{
                                        height: `${(c.value / max) * 100}%`,
                                        background: `linear-gradient(180deg, ${c.color} 0%, ${c.color}AA 100%)`,
                                        boxShadow: `0 -6px 20px -4px ${c.color}55`,
                                    }}
                                />
                            </div>
                            <span className="mt-2 text-[10px] text-slate-600 dark:text-slate-400 text-center leading-tight">{c.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// Main Component
const PerformanceAnalytics = () => {
    const [loading, setLoading] = useState(true);
    const [openPicker, setOpenPicker] = useState(null); // { card: string, type: 'from' | 'to' }
    
    // Date states for each card
    const [laneFromDate, setLaneFromDate] = useState(getMonthsAgoForInput(3));
    const [laneToDate, setLaneToDate] = useState(getTodayForInput());
    const [carrierFromDate, setCarrierFromDate] = useState(getMonthsAgoForInput(3));
    const [carrierToDate, setCarrierToDate] = useState(getTodayForInput());
    const [modeFromDate, setModeFromDate] = useState(getMonthsAgoForInput(3));
    const [modeToDate, setModeToDate] = useState(getTodayForInput());
    const [bookingsFromDate, setBookingsFromDate] = useState(getMonthsAgoForInput(3));
    const [bookingsToDate, setBookingsToDate] = useState(getTodayForInput());

    useEffect(() => {
        setTimeout(() => setLoading(false), 500);
    }, []);

    const handleFromClick = (card) => {
        setOpenPicker({ card, type: 'from' });
    };

    const handleToClick = (card) => {
        setOpenPicker({ card, type: 'to' });
    };

    const handleDateChange = (card, type, value) => {
        switch (card) {
            case 'lane':
                if (type === 'from') setLaneFromDate(value);
                else setLaneToDate(value);
                break;
            case 'carrier':
                if (type === 'from') setCarrierFromDate(value);
                else setCarrierToDate(value);
                break;
            case 'mode':
                if (type === 'from') setModeFromDate(value);
                else setModeToDate(value);
                break;
            case 'bookings':
                if (type === 'from') setBookingsFromDate(value);
                else setBookingsToDate(value);
                break;
        }
        setOpenPicker(null);
    };

    const getCurrentDateForPicker = () => {
        if (!openPicker) return getTodayForInput();
        const { card, type } = openPicker;
        switch (card) {
            case 'lane': return type === 'from' ? laneFromDate : laneToDate;
            case 'carrier': return type === 'from' ? carrierFromDate : carrierToDate;
            case 'mode': return type === 'from' ? modeFromDate : modeToDate;
            case 'bookings': return type === 'from' ? bookingsFromDate : bookingsToDate;
            default: return getTodayForInput();
        }
    };

    if (loading) {
        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse">
                        <div className="h-80 bg-slate-100 dark:bg-slate-800"></div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6">
            <LanePerformance 
                fromDate={laneFromDate} 
                toDate={laneToDate}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                activePicker={openPicker}
            />
            <OnTimeCarriers 
                fromDate={carrierFromDate} 
                toDate={carrierToDate}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                activePicker={openPicker}
            />
            <ShipmentMode 
                fromDate={modeFromDate} 
                toDate={modeToDate}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                activePicker={openPicker}
            />
            <BookingsByCarrier 
                fromDate={bookingsFromDate} 
                toDate={bookingsToDate}
                onFromClick={handleFromClick}
                onToClick={handleToClick}
                activePicker={openPicker}
            />

            {/* Global Date Picker Modal */}
            <AnimatePresence>
                {openPicker && (
                    <>
                        <div 
                            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" 
                            onClick={() => setOpenPicker(null)} 
                        />
                        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
                            <div className="pointer-events-auto">
                                <CustomDatePicker
                                    value={getCurrentDateForPicker()}
                                    onChange={(value) => handleDateChange(openPicker.card, openPicker.type, value)}
                                    onClose={() => setOpenPicker(null)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PerformanceAnalytics;